import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertTriggers,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
    40,
  ]
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
              console.log('✅ 強制加上 mobile class 避免抖動')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf / 字串插值會由工具自動 -1 還原為 0-based
  // 民國年(q8)、西元年齡拆成 q12+q13、性別(q14)
  const isWantsPregnant =
    '(({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)) and ({q14} = 2) and ({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q182} = 1) and ({q185} = 4 or {q185} = 5 or {q185} = 6)'
  const isPostmenopausal =
    '(({q8} >= 6 and {q8} <= 25) or ({q12} >= 6 and {q12} <= 25) or ({q13} >= 6 and {q13} <= 25)) and ({q14} = 2) and ({q185} = 1)'
  const isRecentDrinker = '({q257} = 1 or {q257} = 2)'

  const visibleIfConditionsInit = convertVisibleIf({
    // q6: '{q5} = 2',
    q7: '{q6} = 1',
    q8: '({q6} = 1)',
    q9: '({q6} = 1)',
    q10: '({q6} = 1)',
    q11: '({q6} = 2)',
    q12: '({q11} = 2)',
    q13: '({q11} = 1)',
    q16: '{q15} = 8 or {q15} = 11 or {q15} = 14 or {q15} = 16 or {q15} = 18 or {q15} = 19',
    q18: '{q17} = 1',
    q19: '{q17} = 2',
    // A5
    q21: '{q20} = 1',
    q22: '({q20} = 1) and ({q21} anyof [1])',
    q23: '({q20} = 1) and ({q21} anyof [2])',
    q24: '({q20} = 1) and ({q21} anyof [3])',
    q25: '({q20} = 1) and ({q21} anyof [4])',
    q26: '({q20} = 1) and ({q21} anyof [5])',
    q27: '({q20} = 1) and ({q21} anyof [6])',
    q28: '({q20} = 1) and ({q21} anyof [7])',
    q29: '({q20} = 1) and ({q21} anyof [8])',
    q30: '({q20} = 1) and ({q21} anyof [9])',
    q31: '({q20} = 1) and ({q21} anyof [10])',
    q32: '({q20} = 1) and ({q21} anyof [11])',
    q33: '({q20} = 1) and ({q21} anyof [12])',
    q34: '({q20} = 1) and ({q21} anyof ["other"])',
    q35: '{q20} = 1',
    // A6
    q37: '{q36} = 1 or {q36} = 4 or {q36} = 5 or {q36} = 6 or {q36} = "other"',
    q38: '{q36} = 2 or {q36} = 3 or {q37} = 2',
    q39: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 1)',
    q40: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 2 or {q38} = 3)',
    q41: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q38} = 1 or {q38} = 2 or {q38} = 3)',
    q42: '{q36} = 2 or {q36} = 3 or {q37} = 2',
    q43: '({q36} = 2 or {q36} = 3 or {q37} = 2) and ({q42} = 8 or {q42} = 11 or {q42} = 14 or {q42} = 16 or {q42} = 18 or {q42} = 19)',
    // A7
    q45: '{q44} = 1',
    q46: '{q44} = 2 or {q44} = 3',
    q48: '{q47} = 1',
    q49: '{q47} = 2 or {q47} = 3',
    // B3
    q53: '{q52} = 2',
    q54: '({q52} = 2) and ({q53} = 2)',
    q55: '({q52} = 2) and ({q53} = 2) and ({q54} = 2)',
    q56: '{q52} = 2',
    // B4
    q58: '{q57} = 2',
    q59: '({q57} = 2) and ({q58} = 2)',
    q60: '({q57} = 2) and ({q58} = 2)',
    q61: '({q57} = 2) and ({q58} = 2) and ({q60} = 2)',
    q62: '{q57} = 2',
    q63: '{q57} = 2',
    q64: '{q57} = 2',
    q65: '({q57} = 2) and ({q64} = 2)',
    q66: '{q57} = 2',
    q67: '{q57} = 2',
    q68: '({q57} = 2) and ({q67} = 2)',
    q69: '{q57} = 2',
    q70: '({q57} = 2) and ({q69} = 2)',
    // B5
    q72: '{q71} = 2',
    q73: '({q71} = 2) and ({q72} = 2)',
    q74: '({q71} = 2) and ({q72} = 2) and ({q73} = 2)',
    q75: '{q71} = 2',
    // B6
    q76: '({q52} = 2) or ({q57} = 2) or ({q71} = 2)',
    // B7
    q78: '{q77} = 2',
    q79: '{q77} = 2',
    q80: '{q77} = 2',
    q81: '({q77} = 2) and ({q80} = "other")',
    q82: '({q77} = 2) and ({q80} = 1)',
    q83: '{q77} = 2',
    q84: '({q77} = 2) and ({q83} = 2)',
    // B8
    q86: '{q85} = 2',
    q87: '{q86} = 2',
    q88: '{q86} = 2',
    q89: '{q86} = 2',
    q90: '{q86} = 2',
    q91: '{q86} = 2',
    q92: '{q86} = 2',
    q93: '{q86} = 2',
    // B9
    q99: '{q98} = 2',
    // B10
    q101: '{q100} = 2',
    q102: '{q100} = 2',
    q103: '({q100} = 2) and ({q102} = 2)',
    q104: '({q100} = 2) and ({q102} = 2) and ({q103} = 1)',
    q105: '({q100} = 2) and ({q102} = 2) and ({q103} = 2)',
    q106: '({q100} = 2) and ({q102} = 2)',
    // B11
    q108: '{q107} = 2',
    q109: '{q107} = 2',
    q110: '({q107} = 2) and ({q109} = 2)',
    q111: '({q107} = 2) and ({q109} = 2) and ({q110} = 1)',
    q112: '({q107} = 2) and ({q109} = 2) and ({q110} = 2)',
    q113: '({q107} = 2) and ({q109} = 2)',
    q114: '({q107} = 2) and ({q109} = 2) and ({q110} = 2)',
    // B11 續
    q119: '{q118} = 2',
    q120: '{q118} = 2',
    q121: '({q118} = 2) and ({q120} = 2)',
    q122: '{q118} = 2',
    // B12 男/女 疾病主題索引
    q123: '{q14} = 1 or {q14} = 3',
    q124: '{q14} = 2',
    q125: '({q123} anyof [6]) or ({q124} anyof [6])',
    q126: '({q123} anyof [6]) or ({q124} anyof [6])',
    // B12 心臟
    q127: '({q123} anyof [1]) or ({q124} anyof [1])',
    q128: '({q123} anyof [1]) or ({q124} anyof [1])',
    q129: '({q123} anyof [1]) or ({q124} anyof [1])',
    // B12 痛風
    q130: '({q123} anyof [2]) or ({q124} anyof [2])',
    q131: '({q123} anyof [2]) or ({q124} anyof [2])',
    q132: '({q123} anyof [2]) or ({q124} anyof [2])',
    // B12 胃潰瘍/十二指腸潰瘍
    q133: '({q123} anyof [3]) or ({q124} anyof [3])',
    q134: '({q123} anyof [3]) or ({q124} anyof [3])',
    q135: '({q123} anyof [3]) or ({q124} anyof [3])',
    // B12 肝膽疾病
    q136: '({q123} anyof [4]) or ({q124} anyof [4])',
    q137: '({q123} anyof [4]) or ({q124} anyof [4])',
    q138: '({q123} anyof [4]) or ({q124} anyof [4])',
    // B12 骨質疏鬆
    q139: '({q123} anyof [5]) or ({q124} anyof [5])',
    q140: '({q123} anyof [5]) or ({q124} anyof [5])',
    // B12 癌症
    q141: '({q123} anyof [6]) or ({q124} anyof [6])',
    q142: '({q123} anyof [6]) or ({q124} anyof [6])',
    q143: '({q123} anyof [6]) or ({q124} anyof [6])',
    q144: '({q123} anyof [6]) or ({q124} anyof [6]) and ({q126} != 21)',
    q145: '({q123} anyof [6]) or ({q124} anyof [6]) and ({q126} != 21)',
    q146: '({q123} anyof [6]) or ({q124} anyof [6]) and ({q126} != 21)',
    // B12 關節炎
    q147: '({q123} anyof [7]) or ({q124} anyof [7])',
    q148: '({q123} anyof [7]) or ({q124} anyof [7])',
    q149: '({q123} anyof [7]) or ({q124} anyof [7])',
    // B12 憂鬱症
    q150: '({q123} anyof [8]) or ({q124} anyof [8])',
    q151: '({q123} anyof [8]) or ({q124} anyof [8])',
    q152: '({q123} anyof [8]) or ({q124} anyof [8])',
    // B12 思覺失調
    q153: '({q123} anyof [9]) or ({q124} anyof [9])',
    q154: '({q123} anyof [9]) or ({q124} anyof [9])',
    q155: '({q123} anyof [9]) or ({q124} anyof [9])',
    // B12 其他精神
    q156: '({q123} anyof [10]) or ({q124} anyof [10])',
    q157: '({q123} anyof [10]) or ({q124} anyof [10])',
    q158: '({q123} anyof [10]) or ({q124} anyof [10])',
    // B12 攝護腺【男】
    q159: '({q123} anyof [11]) and ({q14} = 1)',
    q160: '({q123} anyof [11]) and ({q14} = 1)',
    q161: '({q123} anyof [11]) and ({q14} = 1)',
    // 子宮卵巢【女】
    q162: '({q124} anyof [11]) and ({q14} = 2)',
    q163: '({q124} anyof [11]) and ({q14} = 2)',
    q164: '({q124} anyof [11]) and ({q14} = 2)',
    // B13
    q168: '{q167} = 1',
    q169: '{q167} = 2 or {q167} = 3 or {q167} = 4',
    q170: '({q168} = 2 or {q168} = 3 or {q168} = 4) or ({q169} = 2 or {q169} = 3 or {q169} = 4)',
    q171: '{q170} = 2',
    // B14
    q173: '{q172} = 2 or {q172} = "other"',
    q174: '({q172} = 2 or {q172} = "other") and ({q173} anyof [1])',
    q175: '({q172} = 2 or {q172} = "other") and ({q173} anyof [2] or {q174} = 1)',
    q178: '({q8} >= 16 and {q8} <= 47) or ({q12} >= 16 and {q12} <= 47) or ({q13} >= 16 and {q13} <= 47)',
    q179: '{q178} = 2',
    q180: '{q178} = 2',
    q181: '{q178} = 2',
    q183: '{q182} = 2',
    q184: '{q182} = 2',
    // B16
    q185: '{q14} = 2',
    // B17
    q186: isWantsPregnant,
    q187: isWantsPregnant + ' and ({q186} = 2 or {q186} = 3)',
    q188: isWantsPregnant + ' and ({q186} = 2 or {q186} = 3) and ({q187} = 2)',
    q189: isWantsPregnant + ' and ({q186} = 2 or {q186} = 3) and ({q187} = 1)',
    // B18
    q190: isPostmenopausal,
    q191: isPostmenopausal,
    q192: isPostmenopausal + ' and ({q191} = 2)',
    q193: isPostmenopausal + ' and ({q191} = 2)',
    q194: isPostmenopausal + ' and ({q191} = 2) and ({q193} = 2)',
    q195: isPostmenopausal + ' and ({q191} = 2)',
    q196: isPostmenopausal + ' and ({q191} = 2) and ({q195} = 2)',
    q197: isPostmenopausal + ' and ({q191} = 1 or {q191} = 2 or {q191} = 4)',
    q198: isPostmenopausal + ' and ({q191} = 1 or {q191} = 2 or {q191} = 4)',
    q199: isPostmenopausal + ' and ({q191} = 1 or {q191} = 2 or {q191} = 4)',
    // B19
    q201: '{q200} = 1',
    q202: '{q200} = 2 or {q200} = 3 or {q200} = 4',
    // C1
    q208: '{q207} = 3',
    q215: '{q214} = 2',
    q217: '{q216} = 2',
    q219: '{q218} = 2',
    q221: '{q220} = 2',
    // C4
    q224: '(({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)) and {q223} = 2',
    q225: '{q223} = 2 and {q224} = 1',
    // C5
    q227: '{q226} = 2',
    q228: '{q226} = 1',
    // C6
    q230: '{q229} = 2',
    q231: '{q229} = 2',
    // C7
    q233: '{q232} = 2',
    // C8
    q234: '(({q8} >= 21 and {q8} <= 25) or ({q12} >= 21 and {q12} <= 25) or ({q13} >= 21 and {q13} <= 25)) and {q14} = 2',
    q235: '((({q8} >= 1 and {q8} <= 20) or ({q12} >= 1 and {q12} <= 20) or ({q13} >= 1 and {q13} <= 20)) and {q14} = 2) or ({q234} = 2)',
    q236: '{q235} = 2',
    q237: '({q235} = 1) or ({q236} = 3 or {q236} = 4 or {q236} = 5)',
    q238: '({q235} = 3 or {q235} = 4 or {q235} = 5) or ({q236} = 3 or {q236} = 4 or {q236} = 5)',
    q239: '{q238} = 2',
    // C9
    q240: '({q8} >= 20 and {q8} <= 47) or ({q12} >= 20 and {q12} <= 47) or ({q13} >= 20 and {q13} <= 47)',
    q241: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q242: '{q241} = 2',
    q243: '{q241} = 1 or {q242} = 4 or {q242} = 5',
    q244: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and {q14} = 2',
    q245: '{q244} = 2',
    // C10
    q246: '({q8} >= 1 and {q8} <= 15) or ({q12} >= 1 and {q12} <= 15) or ({q13} >= 1 and {q13} <= 15)',
    q247: '{q246} = 2',
    q248: '{q246} = 1',
    q249: '({q8} >= 1 and {q8} <= 25) or ({q12} >= 1 and {q12} <= 25) or ({q13} >= 1 and {q13} <= 25)',
    q250: '{q249} = 2',
    // C11
    q251: '({q8} >= 1 and {q8} <= 45) or ({q12} >= 1 and {q12} <= 45) or ({q13} >= 1 and {q13} <= 45)',
    q252: '{q251} = 2',
    q253: '{q251} = 2',
    q254: '{q251} = 2',
    // D1
    q256: '{q255} = 2',
    q257: '{q255} = 2',
    q258: '({q255} = 2) and ({q257} = 1 or {q257} = 2 or {q257} = 3 or {q257} = 4)',
    q259: isRecentDrinker,
    q260: isRecentDrinker + ' and ({q259} anyof [1])',
    q261: isRecentDrinker + ' and ({q259} anyof [1])',
    q262: isRecentDrinker + ' and ({q259} anyof [1])',
    q263: isRecentDrinker + ' and ({q259} anyof [2])',
    q264: isRecentDrinker + ' and ({q259} anyof [2])',
    q265: isRecentDrinker + ' and ({q259} anyof [2])',
    q266: isRecentDrinker + ' and ({q259} anyof [3])',
    q267: isRecentDrinker + ' and ({q259} anyof [3])',
    q268: isRecentDrinker + ' and ({q259} anyof [3])',
    q269: isRecentDrinker + ' and ({q259} anyof [4])',
    q270: isRecentDrinker + ' and ({q259} anyof [4])',
    q271: isRecentDrinker + ' and ({q259} anyof [4])',
    q272: isRecentDrinker + ' and ({q259} anyof [5])',
    q273: isRecentDrinker + ' and ({q259} anyof [5])',
    q274: isRecentDrinker + ' and ({q259} anyof [5])',
    q275: isRecentDrinker + ' and ({q259} anyof [6])',
    q276: isRecentDrinker + ' and ({q259} anyof [6])',
    q277: isRecentDrinker + ' and ({q259} anyof [6])',
    q278: isRecentDrinker + ' and ({q259} anyof [7])',
    q279: isRecentDrinker + ' and ({q259} anyof [7])',
    q280: isRecentDrinker + ' and ({q259} anyof [7])',
    q281: isRecentDrinker + ' and ({q259} anyof [8])',
    q282: isRecentDrinker + ' and ({q259} anyof [8])',
    q283: isRecentDrinker + ' and ({q259} anyof [8])',
    q284: isRecentDrinker + ' and ({q259} anyof ["other"])',
    q285: isRecentDrinker + ' and ({q259} anyof ["other"])',
    q286: isRecentDrinker + ' and ({q259} anyof ["other"])',
    q287: isRecentDrinker,
    q288: isRecentDrinker + ' and ({q287} = 1)',
    q289: isRecentDrinker + ' and ({q287} = 1)',
    q290: isRecentDrinker + ' and ({q287} = 1)',
    q291: isRecentDrinker + ' and ({q287} = 2)',
    q292: isRecentDrinker + ' and ({q287} = 2)',
    q293: isRecentDrinker + ' and ({q287} = 2)',
    q294: isRecentDrinker + ' and ({q287} = 3)',
    q295: isRecentDrinker + ' and ({q287} = 3)',
    q296: isRecentDrinker + ' and ({q287} = 3)',
    q297: isRecentDrinker + ' and ({q287} = 4)',
    q298: isRecentDrinker + ' and ({q287} = 4)',
    q299: isRecentDrinker + ' and ({q287} = 4)',
    q300: isRecentDrinker + ' and ({q287} = 5)',
    q301: isRecentDrinker + ' and ({q287} = 5)',
    q302: isRecentDrinker + ' and ({q287} = 5)',
    q303: isRecentDrinker + ' and ({q287} = 6)',
    q304: isRecentDrinker + ' and ({q287} = 6)',
    q305: isRecentDrinker + ' and ({q287} = 6)',
    q306: isRecentDrinker + ' and ({q287} = 7)',
    q307: isRecentDrinker + ' and ({q287} = 7)',
    q308: isRecentDrinker + ' and ({q287} = 7)',
    q309: isRecentDrinker + ' and ({q287} = 8)',
    q310: isRecentDrinker + ' and ({q287} = 8)',
    q311: isRecentDrinker + ' and ({q287} = 8)',
    q312: isRecentDrinker + ' and ({q287} = 9)',
    q313: isRecentDrinker + ' and ({q287} = 9)',
    q314: isRecentDrinker + ' and ({q287} = 9)',
    q315: isRecentDrinker,
    q316: '({q255} = 2) and ({q257} = 1 or {q257} = 2 or {q257} = 3 or {q257} = 4)',
    q317: '({q255} = 2) and ({q257} = 1 or {q257} = 2 or {q257} = 3 or {q257} = 4) and ({q316} = 2 or {q316} = 3)',
    q318: '({q255} = 2) and ({q257} = 1 or {q257} = 2 or {q257} = 3 or {q257} = 4)',
    q319: '({q255} = 2) and ({q257} = 1 or {q257} = 2 or {q257} = 3 or {q257} = 4) and ({q318} = 2)',
    q322: '({q321} = 101)', // 隱藏
    // D2
    q324: '{q323} = 2 or {q323} = 3',
    q325: '({q323} = 2 or {q323} = 3) and ({q324} = 2)',
    q326: '({q323} = 2 or {q323} = 3) and ({q324} = 1)',
    q327: '({q323} = 2 or {q323} = 3) and ({q324} = 2)',
    q328: '({q323} = 2 or {q323} = 3)',
    q329: '({q323} = 2 or {q323} = 3) and ({q328} = "other")',
    q330: '({q323} = 2 or {q323} = 3) and ({q328} = "other") and ({q329} = 2)',
    // D2e
    q332: '{q331} = 2 or {q331} = 3',
    q333: '({q331} = 2 or {q331} = 3) and ({q332} = 2 or {q332} = 3)',
    q335: '{q334} = 2 or {q334} = 3',
    q336: '({q334} = 2 or {q334} = 3) and ({q335} = 2 or {q335} = 3)',
    q338: '{q337} = 2 or {q337} = 3',
    q339: '({q337} = 2 or {q337} = 3) and ({q338} = 2 or {q338} = 3)',
    q341: '{q340} = 2 or {q340} = 3',
    q342: '({q340} = 2 or {q340} = 3) and ({q341} = 2 or {q341} = 3)',
    q344: '{q343} = 2 or {q343} = 3',
    q345: '({q343} = 2 or {q343} = 3) and ({q344} = 2 or {q344} = 3)',
    q347: '{q346} = 2 or {q346} = 3',
    q348: '({q346} = 2 or {q346} = 3) and ({q347} = 2 or {q347} = 3)',
    q350: '{q349} = "other"',
    q351: '{q349} = "other"',
    q352: '({q349} = "other") and ({q351} = 2 or {q351} = 3)',
    // D3
    q354: '{q353} = 2',
    q355: '{q353} = 2 and {q354} = 2',
    q356: '{q353} = 2',
    q357: '{q353} = 2',
    q358: '{q353} = 2',
    q359: '{q353} = 2',
    q360: '{q353} = 2',
    // D4
    q362: '{q361} = 3',
    q363: '({q361} = 3) and ({q362} = 1)',
    q364: '({q361} = 3) and ({q362} = 1)',
    q365: '({q361} = 3) and ({q362} = 1)',
    q366: '({q361} = 3) and ({q362} = 1)',
    q367: '({q361} = 3) and ({q362} = 1)',
    q368: '({q361} = 3) and ({q362} = 2)',
    q369: '({q361} = 3) and ({q362} = 2) and ({q368} = 2 or {q368} = 3 or {q368} = 4 or {q368} = 5)',
    q370: '({q361} = 3) and ({q362} = 2) and ({q368} = 2 or {q368} = 3 or {q368} = 4 or {q368} = 5) and ({q369} = 1)',
    q371: '({q361} = 3) and ({q362} = 2)',
    q372: '({q361} = 3) and ({q362} = 2)',
    q373: '({q361} = 3) and ({q362} = 2) and ({q372} = 2)',
    q374: '({q361} = 3) and ({q362} = 2)',
    q375: '({q361} = 3) and ({q362} = 2)',
    q376: '({q361} = 3) and ({q362} = 2) and ({q375} = 2)',
    q377: '({q361} = 3) and ({q362} = 2) and ({q375} = 2)',
    q378: '({q361} = 3) and ({q362} = 2) and ({q375} = 2)',
    q379: '({q361} = 3) and ({q362} = 2)',
    q380: '({q361} = 3) and ({q362} = 2)',
    // D5
    q383: '(({q8} >= 1 and {q8} <= 35) or ({q12} >= 1 and {q12} <= 35) or ({q13} >= 1 and {q13} <= 35)) and ({q323} = 3 or {q361} = 3) or (({q8} >= 35 and {q8} <= 47) or ({q12} >= 35 and {q12} <= 47) or ({q13} >= 35 and {q13} <= 47)) and ({q361} = 3) and ({q18} = 2)',
    q384: '{q383} = 2',
    q385: '({q383} = 1) or ({q383} = 2 and ({q384} = 3 or {q384} = 4 or {q384} = 5))',
    // D6
    q387: '{q386} = 1',
    q389: '{q388} = 2',
    // D7
    q394: '{q393} = 1 or {q393} = 2',
    // D8
    q396: '{q395} = 2 or {q395} = 3 or {q395} = 4 or {q395} = 5 or {q395} = 6 or {q395} = 7 or {q395} = 8',
    q398: '{q397} = 2 or {q397} = 3 or {q397} = 4 or {q397} = 5 or {q397} = 6 or {q397} = 7 or {q397} = 8',
    // D10
    q401: '{q400} = 2',
    q402: '{q400} = 2',
    q404: '{q403} = 2',
    q405: '{q403} = 2',
    q407: '{q406} = 2',
    q408: '{q407} = 2 or {q407} = 3 or {q407} = 4 or {q407} = 5 or {q407} = 6 or {q407} = 7 or {q407} = 8',
    q409: '{q406} = 2',
    q410: '{q409} = 2 or {q409} = 3 or {q409} = 4 or {q409} = 5 or {q409} = 6 or {q409} = 7 or {q409} = 8',
    q412: '{q411} = 2',
    q413: '{q411} = 2',
    q415: '{q414} = 2',
    q416: '{q414} = 2',
    // D12
    q419: '{q418} = 2',
    q420: '{q418} = 2',
    q421: '({q418} = 2) and ({q420} = 1 or {q420} = 2 or {q420} = 3 or {q420} = 4 or {q420} = 6)',
    // G1
    q433: '{q432} = 1',
    q434: '{q432} = 2',
    q435: '{q432} = 3',
    q436: '{q432} = 3',
    q437: '{q432} = 3',
    q438: '{q432} = 3',
    q439: '{q432} = 3',
    q440: '({q432} = 3) and ({q439} = 1)',
    q441: '({q432} = 3) and ({q439} = 2 or {q439} = 4)',
    q442: '{q432} = 3',
    // G2
    q444: '{q443} = 1 or {q443} = 3 or {q443} = "other"',
    q448: '{q447} = 2',
    // G6 → G6a 電梯：選 1 公寓(5樓以下) 或 2 透天 才顯示
    q452: '{q451} = 1 or {q451} = 2 or {q451} = "other"',
    // I0 → 選 6 都不想要 → 跳至 I2，隱藏 I1 系列
    q458: '{q457} notcontains ["none"]',
    q459: '{q458} = 2',
    q460: '{q458} = 2',
  })

  // 非必填（1-based 寫法，convertNotRequired 會 -1）
  // 原 0-based [381] → new 0-based 385 → 1-based 386
  const notRequired = convertNotRequired([386])

  const mappedRateChoice = null
  const pageBreakQuestions = null

  const triggersConditionsInit = convertTriggers([])
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  // 結尾語：國健署宣導
  const endMessage = `<div>我們的訪問到這邊告一段落，非常謝謝您的協助！<br><br>♥國民健康署祝您健康快樂♥<br><br><br>相關健康資訊可至國民健康署網站查詢<br><br>(1)可選下方按鈕，自行留存「受訪者同意書」。<br><a href="#" onclick="return false;" style="display:inline-block;padding:8px 16px;background-color:#4CAF50;color:#fff;text-decoration:none;border-radius:4px;margin-top:8px;">下載同意書留存</a><br><br>(2)<span style="color:#EA0000">好禮抽抽週活動</span>，請詳見活動說明網頁：<br>👉<a href="/P260601122243471/lottery" target="_blank">${window.location.origin}/P260601122243471/lottery</a></div>`

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    setCompletionMessage(endMessage)
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
